import { unzip, ZipEntry, ZipInfo } from 'unzipit';
import type { ZipBinaryStorageType } from '../interface/zip';

function _zipFile(srcData: Blob | ArrayBuffer): Promise<ZipInfo> {
    return unzip(srcData);
}

function _isBlob(object: unknown): object is Blob {
    return (
        object instanceof Blob ||
        ['[object Blob]', '[object File]'].indexOf(Object.prototype.toString.call(object)) !== -1
    );
}

function _isArrayBuffer(object: unknown): object is ArrayBuffer {
    return object instanceof ArrayBuffer || object instanceof Buffer;
}

function _unzipEntry<T extends ZipBinaryStorageType>(srcData: T, entry: ZipEntry): Promise<T> {
    return new Promise(async function (resolve, reject) {
        try {
            if (_isBlob(srcData)) {
                const result = await entry.blob();
                resolve(result as T);
            } else if (_isArrayBuffer(srcData)) {
                const result = await entry.arrayBuffer();
                resolve(result as T);
            } else {
                reject(new Error(`unknown zip src-data type ${srcData}`));
            }
        } catch (error: any) {
            reject(error);
        }
    });
}

function _unzipFile<T extends ZipBinaryStorageType>(srcData: T, fileName: string): Promise<T> {
    return new Promise(async function (resolve, reject) {
        try {
            const zipFile = await _zipFile(srcData);

            const matchedEntries = Object.entries(zipFile.entries)
                .filter(entry => entry[1].name === fileName)
                .map(val => val[1]);

            if (matchedEntries.length === 0) {
                reject('No matching files found');
            } else {
                const result = await _unzipEntry<T>(srcData, matchedEntries[0]);
                resolve(result);
            }
        } catch (error) {
            reject(error);
        }
    });
}

function _unzipAll<T extends ZipBinaryStorageType>(srcData: T): Promise<Record<string, T>> {
    return new Promise(async function (resolve, reject) {
        try {
            const zipFile = await _zipFile(srcData);

            const items: Record<string, T> = {};

            Object.entries(zipFile.entries).forEach(async function (entry) {
                const data = await _unzipEntry(srcData, entry[1]);
                items[entry[1].name] = data;
            });

            resolve(items);
        } catch (error) {
            reject(error);
        }
    });
}

function _listings(blobData: ZipBinaryStorageType): Promise<Array<string>> {
    return new Promise(async function (resolve, reject) {
        try {
            const zipFile = await _zipFile(blobData);
            const entries = Object.entries(zipFile.entries).map(entry => entry[1].name);
            resolve(entries);
        } catch (error) {
            reject(error);
        }
    });
}

const module = {
    unzipFile: _unzipFile,
    unzipAll: _unzipAll,
    listings: _listings,
};

export default module;
